import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  authRoles,
  commentTypes,
  useDate,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type {
  TCity,
  TComment,
  TEmail,
  TFile,
  TInvitation,
  TLead,
} from '../../..'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TRecordAction } from '@/core/types'
import { useInvitationStore } from '../../store/invitation'
import { storeToRefs } from 'pinia'
import { useCommentsQuery } from '../query/useCommentsQuery'
import { invitationStatusesIds } from '@/core'
import { useFilesQuery } from '../query/useFilesQuery'
import { useLeadQuery } from '../query/useLeadQuery'
import { useCityQuery } from '../query/useCityQuery'
import { useAuth } from 'vue-auth3'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TInvitation>({})

  const auth = useAuth()
  const storeDetails = useInvitationStore()
  const { invitations } = storeToRefs(storeDetails)

  const { zonedTimeToUtc } = useDate()
  const modulePagination = usePagination()

  const filters = ref<ToQueryStringFilters>({
    search: '',
    delivery_date: '',
    state_id: [],
    city_id: [],
    status_id: [],
    lead_id: [],
    order_by: 'delivery_date,asc',
  })

  const citiesFilters = ref<ToQueryStringFilters>({
    param: '',
    state_id: [],
    order_by: 'name,asc',
  })

  const estimationCommentsFilters = ref<ToQueryStringFilters>({
    invitation_id: [],
  })

  const sellerCommentsFilters = ref<ToQueryStringFilters>({
    invitation_id: [],
  })

  const filesFilters = ref<ToQueryStringFilters>({
    invitation_id: '',
  })

  const leadsFilters = ref<ToQueryStringFilters>({
    search: '',
    order_by: 'created_at,desc',
  })

  const statusFilters = ref<ToQueryStringFilters>({
    id: [
      invitationStatusesIds.in_progress,
      invitationStatusesIds.sent_on_time,
      invitationStatusesIds.sent_wrong_time,
      invitationStatusesIds.won,
      invitationStatusesIds.lost,
      invitationStatusesIds.expired,
      invitationStatusesIds.hot,
      invitationStatusesIds.cancelled,
    ],
  })

  const moduleQueryParams = useToQueryString(toReactive(filters))
  const cityQueryParams = useToQueryString(toReactive(citiesFilters))
  const leadQueryParams = useToQueryString(toReactive(leadsFilters))
  const estimationCommentsQueryParams = useToQueryString(
    toReactive(estimationCommentsFilters),
  )
  const sellerCommentsQueryParams = useToQueryString(
    toReactive(sellerCommentsFilters),
  )
  const statusQueryParams = useToQueryString(toReactive(statusFilters))
  const filesQueryParams = useToQueryString(toReactive(filesFilters))

  const moduleQuery = useModuleQuery({
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      citiesCatalog: cityQueryParams.queryParams,
      statusCatalog: statusQueryParams.queryParams,
      leadsCatalog: leadQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      statusCatalog: true,
      leadsCatalog: true,
      citiesCatalog: computed(() => !!cityQueryParams.queryParams.value),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const commentsQuery = useCommentsQuery({
    filters: {
      loadEstimationComments: estimationCommentsQueryParams.queryParams,
      loadSellerComments: sellerCommentsQueryParams.queryParams,
    },
    enableQuery: {
      loadEstimationComments: computed(
        () =>
          !!estimationCommentsQueryParams.params.value.length &&
          auth.check([authRoles.ESTIMATOR, authRoles.SELLER]),
      ),
      loadSellerComments: computed(
        () => !!sellerCommentsQueryParams.params.value.length,
      ),
    },
  })
  const filesQuery = useFilesQuery({
    filters: {
      loadRecords: filesQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: computed(() => !!filesQueryParams.params.value.length),
    },
  })
  const leadsQuery = useLeadQuery()
  const citiesQuery = useCityQuery()

  const { orderByOptions } = useOrderByFilter({
    columns: ['delivery_date'],
  })

  const getNextPage = async () => {
    await moduleQuery.loadRecords.fetchNextPage()
  }

  const onAdd = async (payload: TInvitation) => {
    await moduleQuery.addRecord.mutateAsync({
      ...payload,
      delivery_date: zonedTimeToUtc(new Date(payload.delivery_date ?? ''), {
        removeTimeZone: true,
      }),
    })
  }

  const onEdit = async (payload: TInvitation) => {
    await moduleQuery.editRecord.mutateAsync({
      ...payload,
      delivery_date: zonedTimeToUtc(new Date(payload.delivery_date ?? ''), {
        removeTimeZone: true,
      }),
      status_id: payload.status_id ?? payload.status?.id,
      priority_id: payload.priority_id ?? payload.priority?.id,
      city_id: payload.city_id ?? payload.city?.id,
      state_id: payload.state_id ?? payload.state?.id,
      lead_id: payload.lead_id ?? payload.lead?.id,
    })
  }

  const onDelete = async (payload: TInvitation) => {
    await moduleQuery.deleteRecord.mutateAsync(payload)
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onAddLead = async (payload: TLead) => {
    await leadsQuery.addRecord.mutateAsync(payload)
    await moduleQuery.leadsCatalog.refetch()
  }

  const onAddCity = async (payload: TCity) => {
    await citiesQuery.addRecord.mutateAsync(payload)
    await moduleQuery.citiesCatalog.refetch()
  }

  const onDeleteManyFiles = async (files: number[]) => {
    await filesQuery.deleteRecords.mutateAsync(files)
  }

  const onAddComment = async (payload: TComment) => {
    await commentsQuery.addRecord
      .mutateAsync({
        ...payload,
        invitation_id: form.value.id,
      })
      .then(() => {
        if (payload.type === commentTypes.ESTIMATION) {
          commentsQuery.loadEstimationComments.refetch()
          return
        }
        commentsQuery.loadSellerComments.refetch()
      })
  }

  const uploadFile = async (payload: TFile) => {
    await filesQuery.addRecord.mutateAsync({
      ...payload,
      invitation_id: form.value.id,
    })
  }

  const onSendEmail = async (payload: TEmail) => {
    await moduleQuery.sendQuotationEmail.mutateAsync(payload)
  }

  const onLoadComments = (id: number) => {
    estimationCommentsFilters.value.invitation_id = [id]
  }

  const onLoadSellerComments = (id: number) => {
    sellerCommentsFilters.value.invitation_id = [id]
  }

  const onLoadFiles = (id: number) => {
    filesFilters.value.invitation_id = id
  }

  const onExport = async () => {
    await moduleQuery.exportRecords.mutateAsync({
      dynamicFilters: [
        moduleQueryParams.queryParams.value,
        'disabled_pagination=1',
      ].join('&'),
    })
  }

  const sellers = computed(() => moduleQuery.sellersCatalog.data?.value)
  const leads = computed(() => moduleQuery.leadsCatalog.data?.value)
  const states = computed(() => moduleQuery.statesCatalog.data?.value)
  const cities = computed(() => moduleQuery.citiesCatalog.data?.value)
  const status = computed(() => moduleQuery.statusCatalog.data?.value)
  const comments = computed(
    () => commentsQuery.loadEstimationComments.data.value,
  )
  const sellerComments = computed(
    () => commentsQuery.loadSellerComments.data.value,
  )
  const files = computed(() => filesQuery.loadRecords.data.value)

  const loaders = computed(() => ({
    isLoading:
      moduleQuery.loadRecords.isLoading.value ||
      moduleQuery.loadRecords.isFetching.value,
    isCommentsLoading: commentsQuery.loadEstimationComments.isLoading.value,
    isCommentsSellerLoading: commentsQuery.loadSellerComments.isLoading.value,
    isCommentsAdding: commentsQuery.addRecord.isLoading.value,
    isFilesLoading: filesQuery.loadRecords.isLoading?.value,
    isStatusLoading: moduleQuery.statusCatalog.isLoading?.value,
    isCitiesLoading: moduleQuery.citiesCatalog.isLoading?.value,
    isStatesLoading: moduleQuery.statesCatalog.isLoading?.value,
    isLeadsLoading: moduleQuery.leadsCatalog.isLoading?.value,
    isSellersLoading: moduleQuery.sellersCatalog.isLoading?.value,
    isAddingInvitation: moduleQuery.addRecord.isLoading?.value,
    isAddingLead: leadsQuery.addRecord.isLoading.value,
    isAddingCity: citiesQuery.addRecord.isLoading.value,
    isUploadingFile: filesQuery.addRecord.isLoading.value,
    isSendingEmail: moduleQuery.sendQuotationEmail.isLoading.value,
    isExporting: moduleQuery.exportRecords.isLoading.value,
  }))

  return {
    loaders,
    files,
    invitations,
    filters,
    orderByOptions,
    rows,
    form,
    sellers,
    status,
    leads,
    comments,
    sellerComments,
    states,
    cities,
    citiesFilters,
    modulePagination: toReactive(modulePagination),
    moduleQueryParams,
    leadsFilters,
    onDeleteManyFiles,
    onExport,
    onAddCity,
    onAction,
    onAddLead,
    onAddComment,
    onDelete,
    onEdit,
    onAdd,
    uploadFile,
    onLoadComments,
    onLoadSellerComments,
    onLoadFiles,
    getNextPage,
    onSendEmail,
  }
})

export { useProvideModule, useModule }
